import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, TrendingUp, ArrowRight, Train, Building2, Sparkles, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area } from "recharts";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";

export default function CityPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [market, setMarket] = useState(null);
  const [summary, setSummary] = useState("");
  const [sumLoading, setSumLoading] = useState(true);

  useEffect(() => {
    setData(null); setSummary(""); setSumLoading(true);
    api.city(slug).then(setData).catch(() => {});
    api.market(slug).then(setMarket).catch(() => {});
    api.localitySummary(slug).then((r) => setSummary(r.summary || "")).catch(() => {}).finally(() => setSumLoading(false));
  }, [slug]);

  if (!data) return <div className="py-32 text-center text-pbs-grey">Loading city…</div>;
  const { city, properties } = data;

  return (
    <div>
      <div className="relative h-64 sm:h-80">
        <img src={city.image} alt={city.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-8">
          <span className="text-pbs-gold text-sm font-bold uppercase tracking-wider">{city.state}</span>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white">Real Estate in {city.name}</h1>
          <p className="text-white/80 mt-1">{properties.length} verified properties · Avg ₹{city.avg_price_sqft.toLocaleString("en-IN")}/sq.ft</p>
        </div>
      </div>

      {/* Market intelligence */}
      {market && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center gap-2 mb-6"><Sparkles size={18} className="text-pbs-purple" /><h2 className="text-2xl font-extrabold text-pbs-dark">Know Your Market</h2></div>
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-pbs-line p-5 pbs-shadow">
              <h3 className="font-bold text-pbs-dark mb-3">Price Trend (₹/sq.ft)</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={market.price_trend}>
                  <defs><linearGradient id="pg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#236B45" stopOpacity={0.3} /><stop offset="100%" stopColor="#236B45" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="year" stroke="#5F6E64" fontSize={12} /><YAxis stroke="#5F6E64" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => `₹${v.toLocaleString("en-IN")}`} />
                  <Area type="monotone" dataKey="price" stroke="#236B45" strokeWidth={2.5} fill="url(#pg)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              <StatCard label="Average Price" value={`₹${city.avg_price_sqft.toLocaleString("en-IN")}`} sub="per sq.ft" />
              <StatCard label="Price Appreciation" value={`+${city.appreciation_pct}%`} sub="year on year" accent />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-5">
            <InfoList icon={Train} title="Connectivity" items={market.connectivity} />
            <InfoList icon={Building2} title="Infrastructure" items={market.infrastructure} />
            <InfoList icon={TrendingUp} title="Upcoming Development" items={market.upcoming} />
          </div>

          {/* AI Market Insight (Gemini) */}
          <div className="mt-5 rounded-2xl p-[1.5px] bg-gradient-to-r from-pbs-purple via-pbs-magenta to-pbs-orange">
            <div className="rounded-2xl bg-white p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-lg pbs-gradient text-white flex items-center justify-center"><Sparkles size={14} /></span>
                <span className="text-sm font-extrabold text-pbs-dark">AI Market Insight</span>
                <span className="text-[10px] font-bold text-pbs-purple bg-pbs-purple/10 px-2 py-0.5 rounded-full uppercase">Powered by Gemini</span>
              </div>
              {sumLoading
                ? <p className="text-sm text-pbs-grey flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Generating market overview…</p>
                : <p className="text-sm text-pbs-grey leading-relaxed">{summary || "Insight will be available soon."}</p>}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-extrabold text-pbs-dark">Properties in {city.name}</h2>
          <Link to={`/properties?city=${encodeURIComponent(city.name)}`} className="text-pbs-purple font-bold text-sm flex items-center gap-1">View all <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {properties.map((p) => <PropertyCard key={p.slug} property={p} compact />)}
        </div>
      </section>
    </div>
  );
}
const StatCard = ({ label, value, sub, accent }) => (
  <div className={`rounded-2xl p-5 ${accent ? "pbs-gradient text-white" : "bg-white border border-pbs-line text-pbs-dark"}`}>
    <p className={`text-sm ${accent ? "text-white/80" : "text-pbs-grey"}`}>{label}</p>
    <p className="text-3xl font-extrabold font-mono mt-1">{value}</p>
    <p className={`text-xs mt-0.5 ${accent ? "text-white/70" : "text-pbs-grey"}`}>{sub}</p>
  </div>
);
const InfoList = ({ icon: Icon, title, items }) => (
  <div className="bg-white rounded-2xl border border-pbs-line p-5">
    <div className="flex items-center gap-2 mb-3"><Icon size={16} className="text-pbs-purple" /><h4 className="font-bold text-pbs-dark text-sm">{title}</h4></div>
    <ul className="space-y-1.5">{items.map((i) => <li key={i} className="text-sm text-pbs-grey flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-pbs-orange" /> {i}</li>)}</ul>
  </div>
);
